import {Color, Material} from "../catalog/catalog.model";

export interface OrderItem {
  id?: number; // for internal front end use
  article?: Article;
  count?: number;
  orderId?: number;
  orderItemId?: number;
  erpOrderId?: string;
  externalOrderId?: string;
  position?: string;
  side?: string;
  orderDate?: Date;
  desireDate?: Date;
  deliveryDate?: Date;
  invoiceDate?: Date;
  chargeNumber?: string;
  deliveryNumber?: string;
  invoiceNumber?: string;
  credit?: string;
  isReturn?: boolean;
  isIndividual?: boolean;
  orderStatus?: OrderStatus;
  itemStatus?: OrderItemStatus;
  copyPossible?: boolean;
  isNew?: boolean;
  isUpdate?: boolean;
  electronicOrdering?: boolean;
}

export interface Order {
  id?: number;
  orderItems: Array<OrderItem>;
}

export interface OrderQuery {
  orderId?: string,
  deliveryNumber?: string,
  commission?: string,
  chargeNumber?: string,
  fromDate?: string,
  toDate?: string,
  articleType?: ArticleType,
  copyPossible?: boolean,
  lensType?: LensType
}

export interface Article {
  readonly id: number,
  readonly articleId?: string,
  readonly type: ArticleType,
  readonly name: string,
  readonly imageUrl?: string,
  readonly backSide?: Backside,
  readonly lensGroup?: string,
  readonly lensType?: LensType,
  readonly clientsGroups?: string,
  readonly price?: string,
  readonly priceInfo?: string
  readonly electronicOrdering?: boolean
}

export interface Backside extends ListItem {
  readonly id: number,
}

export interface ListItem {
  readonly id: string | number,
  readonly name: string
}

export interface CareItem {
  id: number,
  name: string,
  description: string,
  price: string,
  readonly priceInfo: string,
  readonly electronicOrdering: boolean,
  imageUrl: string,
  quantity: number,
  careTypes: Array<CareItemType>,
  units?: number
}

export interface AccessoryItem {
  id: number,
  name: string,
  description: string,
  price: string,
  readonly priceInfo: string,
  readonly electronicOrdering: boolean,
  imageUrl: string,
  units?: number
}

export enum CareItemType {
  HARD = "HARD",
  SOFT = "SOFT",
  EYE = "EYE",
  REWETTING = "REWETTING"
}

export type LensType = "HARD" | "SOFT"

export enum ArticleType {
  LENS = "LENS",
  CARE = "CARE",
  ACCESSORY = "ACCESSORY"
}

export enum OrderStatus {
  DRAFT = "DRAFT",
  SENT = "SENT",
  SYNCED = "SYNCED"
}

export enum OrderItemStatus {
  ORDER_ENTRY = "ORDER_ENTRY",
  PRODUCTION = "PRODUCTION",
  PREPARATION = "PREPARATION",
  DELIVERY = "DELIVERY",
  STORNO = "STORNO",
  ERROR = "ERROR",
  INVOICE = "INVOICE",
  CART = "CART",
  RETURNED = "RETURNED"
}

export interface LensOrderItem extends OrderItem {
  commission?: string;
  desireDate?: Date;
  side?: LensSide;
  material?: Material;
  color?: Color;
  featureValues: FeatureValues;
  decimalFormats?: DecimalFormats;
  isReturn?: boolean;
  isIndividual?: boolean;
}

export type LensSide = "RIGHT" | "LEFT";

export type FeatureValue = {name: string, value: string | number};

export type FeatureValues = { [name: string]: string | number };

export type DecimalFormats = { [name: string]: number };

// api returns dates as strings, so we translate them to actual dates
export function fixDates(item: OrderItem): OrderItem {
  return {
    ...item,
    orderDate: item.orderDate && new Date(item.orderDate),
    deliveryDate: item.deliveryDate && new Date(item.deliveryDate),
    desireDate: item.desireDate && new Date(item.desireDate),
    invoiceDate: item.invoiceDate && new Date(item.invoiceDate),
  }
}

export function isLensOrderItem(item: OrderItem): item is LensOrderItem {
  return item.article && item.article.type === ArticleType.LENS;
}

export function isCareAccessoryOrderItem(item: OrderItem): item is CareAccessoryOrderItem {
  return item.article && (item.article.type === ArticleType.CARE || item.article.type === ArticleType.ACCESSORY);
}

export function featureValuesAsArray(featureValues: FeatureValues): Array<FeatureValue> {
  return Object.keys(featureValues).map(name => <FeatureValue>{name, value: featureValues[name]});
}

export interface CareAccessoryOrderItem extends OrderItem {
}

export interface ProposalResponse {
  ID?: string;
  Material?: string;
  RadiusBasecurve?: string;
  Sphere?: number;
  Diameter?: number;
}

export class ProposalRequest {
  cLType: number;
  designType: number;
  spectacularRefraction: {
    sphere?: number,
    cylinder?: number,
    addition?: number,
    axis?: number,
    vertexDistance?: number
  };
  centralRadius: {
    radiusFlach?: number,
    radiusFlachDegree?: number,
    radiusSteil?: number,
    eccentricity?: number,
    corneaDiamter?: number,
    kcStadium?: number,
  };

  constructor() {
    this.spectacularRefraction = {};
    this.centralRadius = {};
  }
}
